import { createReducer, on } from '@ngrx/store';
import { ResetStateFromRouter } from 'src/app/shared/actions/eliqua.actions';
import { CreateNewIntegration } from '../actions/integration-management-create-new.actions';
import {
  ApiKeyNeverbounceChange,
  BaseUrlNeverbounceChange,
  CloseNeverbounceEditIntegration,
  ConnectionTimeoutNeverbounceChange,
  EditNeverbounceIntegrationSystem,
  JobMaxSizeNeverbounceChange,
  NameNeverbounceChange,
  NeverbounceSubmitData,
  NotificationEmailNeverbounceChange,
  ReadTimeoutNeverbounceChange,
  ResultTypeNeverbounceChange,
  SaveNeverbounceConfigurationFailed,
  SaveNeverbounceConfigurationSuccess,
  StatusNeverbounceChange,
  ToggleCredentialsConfHelpTextStatus,
  ToggleEndpointConfHelpTextStatus,
  ToggleRootConfHelpTextStatus,
  UpdateNeverbounceConfigurationSuccess,
} from '../actions/neverbounce-configuration.actions';

export interface NeverbounceConfigurationState {
  nameNeverbounce: string;
  statusNeverbounce: boolean;
  notificationEmailNeverbounce: string;
  jobMaxSizeNeverbounce: number | null;
  resultTypesNeverbounce: string[];
  baseUrlNeverbounce: string;
  readTimeoutNeverbounce: number | null;
  connectionTimeoutNeverbounce: number | null;
  apiKeyNeverbounce: string;
  showUpdateBtn: boolean;
  disableName: boolean;
  rootConfigurationHelpTextStatus: boolean;
  credentialsConfigurationHelpTextStatus: boolean;
  endpointConfigurationHelpTextStatus: boolean;
  selectedNeverbounceDataFromGrid: any;
  showCancelBtn: boolean;
  isSubmittedData: boolean;
}

const initialState: NeverbounceConfigurationState = {
  nameNeverbounce: '',
  statusNeverbounce: false,
  notificationEmailNeverbounce: '',
  jobMaxSizeNeverbounce: 1000,
  resultTypesNeverbounce: [],
  baseUrlNeverbounce: '',
  readTimeoutNeverbounce: 0,
  connectionTimeoutNeverbounce: 0,
  apiKeyNeverbounce: '',
  showUpdateBtn: false,
  disableName: false,
  rootConfigurationHelpTextStatus: false,
  credentialsConfigurationHelpTextStatus: false,
  endpointConfigurationHelpTextStatus: false,
  selectedNeverbounceDataFromGrid: [],
  showCancelBtn: false,
  isSubmittedData: false,
};

export const NeverbounceConfigurationReducer = createReducer(
  initialState,
  on(NameNeverbounceChange, (state, { nameNeverbounce }) => ({
    ...state,
    nameNeverbounce,
  })),
  on(StatusNeverbounceChange, (state, { statusNeverbounce }) => ({
    ...state,
    statusNeverbounce,
  })),
  on(NotificationEmailNeverbounceChange, (state, { notificationEmailNeverbounce }) => ({
    ...state,
    notificationEmailNeverbounce,
  })),
  on(JobMaxSizeNeverbounceChange, (state, { jobMaxSizeNeverbounce }) => ({
    ...state,
    jobMaxSizeNeverbounce,
  })),
  on(ResultTypeNeverbounceChange, (state, { resultTypesNeverbounce }) => ({
    ...state,
    resultTypesNeverbounce,
  })),
  on(BaseUrlNeverbounceChange, (state, { baseUrlNeverbounce }) => ({
    ...state,
    baseUrlNeverbounce,
  })),
  on(ReadTimeoutNeverbounceChange, (state, { readTimeoutNeverbounce }) => ({
    ...state,
    readTimeoutNeverbounce,
  })),
  on(ConnectionTimeoutNeverbounceChange, (state, { connectionTimeoutNeverbounce }) => ({
    ...state,
    connectionTimeoutNeverbounce,
  })),
  on(ApiKeyNeverbounceChange, (state, { apiKeyNeverbounce }) => ({
    ...state,
    apiKeyNeverbounce,
  })),
  on(SaveNeverbounceConfigurationSuccess, (state, { res }) => ({
    ...state,
    showUpdateBtn: true,
    disableName: true,
    nameNeverbounce: res.payload.data.integrationConfig.integrationDisplayName,
    statusNeverbounce: res.payload.data.deleted,
    notificationEmailNeverbounce: res.payload.data.integrationConfig.notificationEmail,
    jobMaxSizeNeverbounce: res.payload.data.integrationConfig.jobMaxSize,
    resultTypesNeverbounce: res.payload.data.integrationConfig.validResultTypes,
    baseUrlNeverbounce: res.payload.data.endpointConfiguration.endpointConfig.baseURL,
    readTimeoutNeverbounce: res.payload.data.endpointConfiguration.endpointConfig.readTimeOut,
    connectionTimeoutNeverbounce:
      res.payload.data.endpointConfiguration.endpointConfig.connectionTimeOut,
    apiKeyNeverbounce: res.payload.data.endpointConfiguration.credentialConfig.key,
    rootConfigurationHelpTextStatus: false,
    credentialsConfigurationHelpTextStatus: false,
    endpointConfigurationHelpTextStatus: false,
    isSubmittedData: false,
  })),
  on(SaveNeverbounceConfigurationFailed, (state) => ({
    ...state,
    showUpdateBtn: false,
    disableName: false,
  })),
  on(EditNeverbounceIntegrationSystem, (state, { neverbounceData }) => ({
    ...state,
    nameNeverbounce: neverbounceData.integrationConfig.integrationDisplayName,
    statusNeverbounce: neverbounceData.deleted,
    notificationEmailNeverbounce: neverbounceData.integrationConfig.notificationEmail,
    jobMaxSizeNeverbounce: neverbounceData.integrationConfig.jobMaxSize,
    resultTypesNeverbounce: neverbounceData.integrationConfig.validResultTypes,
    baseUrlNeverbounce: neverbounceData.endpointConfiguration.endpointConfig.baseURL,
    readTimeoutNeverbounce: neverbounceData.endpointConfiguration.endpointConfig.readTimeOut,
    connectionTimeoutNeverbounce:
      neverbounceData.endpointConfiguration.endpointConfig.connectionTimeOut,
    apiKeyNeverbounce: neverbounceData.endpointConfiguration.credentialConfig.key,
    showUpdateBtn: true,
    disableName: true,
    showCancelBtn: true,
    isSubmittedData: false,
  })),
  on(CreateNewIntegration, () => ({
    ...initialState,
  })),
  on(UpdateNeverbounceConfigurationSuccess, (state, { res }) => ({
    ...state,
    showUpdateBtn: true,
    disableName: true,
    nameNeverbounce: res.payload.data.integrationConfig.integrationDisplayName,
    statusNeverbounce: res.payload.data.deleted,
    notificationEmailNeverbounce: res.payload.data.integrationConfig.notificationEmail,
    jobMaxSizeNeverbounce: res.payload.data.integrationConfig.jobMaxSize,
    resultTypesNeverbounce: res.payload.data.integrationConfig.validResultTypes,
    baseUrlNeverbounce: res.payload.data.endpointConfiguration.endpointConfig.baseURL,
    readTimeoutNeverbounce: res.payload.data.endpointConfiguration.endpointConfig.readTimeOut,
    connectionTimeoutNeverbounce:
      res.payload.data.endpointConfiguration.endpointConfig.connectionTimeOut,
    apiKeyNeverbounce: res.payload.data.endpointConfiguration.credentialConfig.key,
  })),
  on(ToggleRootConfHelpTextStatus, (state) => ({
    ...state,
    rootConfigurationHelpTextStatus: !state.rootConfigurationHelpTextStatus,
  })),
  on(ToggleCredentialsConfHelpTextStatus, (state) => ({
    ...state,
    credentialsConfigurationHelpTextStatus: !state.credentialsConfigurationHelpTextStatus,
  })),
  on(ToggleEndpointConfHelpTextStatus, (state) => ({
    ...state,
    endpointConfigurationHelpTextStatus: !state.endpointConfigurationHelpTextStatus,
  })),
  on(CloseNeverbounceEditIntegration, () => initialState),
  on(NeverbounceSubmitData, (state) => ({
    ...state,
    isSubmittedData: true,
  })),
  on(ResetStateFromRouter, () => ({
    ...initialState,
  })),
);
